import { z } from "zod";
// The two vocabularies `listBodyAssessments` answers in, imported rather than
// restated: a band or a range model spelled out again here would drift from the
// one the far side stores and the drift would look like a parse failure on the
// AI Worker, a turn later (`T-35`).
import { BODY_FAT_RANGE_MODELS, BODY_WHTR_BANDS, } from "../training/body-assessment";
import { EQUIPMENT_ID_COUNT } from "../training/equipment";
import { HOME_EQUIPMENT_OPTIONS, INTAKE_WRITE_SECTIONS, ONBOARDING_CHOICE_VALUES, } from "../training/intake";
import { onboardingProgress, } from "../training/onboarding";
import { movementPattern, planDayInput, planFinding, planSplit, priorityMuscle, requireDaysWithCustom, } from "../training/plan";
import { ATHLETE_SEXES, MAX_MOTIVATION_LENGTH, OCCUPATIONS, PREVIOUS_MODALITIES, PROTEIN_MEALS, PROTEIN_SUPPLEMENTS, } from "../training/profile";
import { MAX_PRIORITY_MUSCLES } from "../training/protocols";
import { TRAINING_LEVELS, trainingConfigInput, trainingConfigOutput, } from "../training/training-config";
import { weightUnit } from "../weight-unit";
/**
 * The `ai → api` data channel (`D72`).
 *
 * The agent Worker has NO D1 binding and must not grow one — `apps/ai`'s own
 * `wrangler.jsonc` says so. Every path from the agent to any database runs
 * through a service binding into `apps/api`, and this file is the vocabulary
 * both sides of that binding speak. It lives in `@repo/contract` and not in
 * `@repo/agents` because that package declares `portableAcrossRepos: true`, so
 * architecture rule 12 forbids it importing any `@repo/*` but `@repo/kernel`.
 *
 * ── THE ACTOR IS THE FIRST PARAMETER OF EVERY METHOD, AND IT IS NOT A TOOL
 *    PARAMETER ───────────────────────────────────────────────────────────────
 * This is the single control the whole design rests on, and precisely the one
 * the Supabase MCP incident lacked. The account id already exists, correct, at
 * the edge: extracted there, carried in the identity envelope, re-verified in
 * `onConnect` against the Durable Object's own name (`T-11` — edge request
 * properties do not exist inside a Durable Object, so identity travels inward
 * as data). Letting the model supply it instead would discard that guarantee at
 * the last step and re-derive it from a probability distribution, every turn,
 * over text that includes the student's own workout notes — which are
 * attacker-controlled by construction.
 *
 * So: the runtime injects this value from the stamped scope, and **the Zod
 * schema the model sees must never contain either of these fields.** A model
 * that is right 99.9% of the time leaks one person's medical disclosure per
 * thousand turns, silently, with nothing in any log to find.
 */
/**
 * Every write that arrives over this binding is the `agent` path of `D69`'s
 * two-column attribution — *"the agent, at your trainer's request"* is a
 * different answer from *"your trainer"*, because it means a model composed it
 * and a human at most did not disagree.
 *
 * A CONSTANT rather than a parameter: the caller cannot claim to be `direct`,
 * because arriving here is what makes it `agent`. The union it belongs to
 * (`ACTOR_PATHS` in `@repo/training`) is not imported — `@repo/contract`
 * imports nothing outside `shared` — and nothing needs it to be: this side
 * knows only its own half.
 */
export const AGENT_ACTOR_PATH = "agent";
/**
 * Who is asking, and about whom.
 *
 * `subjectAccountId` is a TRAINER reading a student, and `apps/api` verifies the
 * membership server-side before any read (`D50`). It is checked, never
 * trusted — that is unavoidable under any design, SQL included, because a
 * trainer legitimately reads other people's data, so the correct filter is not
 * a constant.
 *
 * `null` — not absent — means "the caller's own data". Required and nullable
 * rather than optional, so a call site says which of the two it means instead
 * of omitting the field and being read as the safer one by accident.
 */
export const agentActor = z.object({
    accountId: z.string().uuid(),
    subjectAccountId: z.string().uuid().nullable(),
});
/**
 * The reply to `ping` — the trivial method that proves the channel,
 * before any read or write exists.
 *
 * It ECHOES the actor rather than returning `true`. A binding that typechecks
 * and is not wired reaches production as a runtime `undefined`, and a method
 * that returns a constant would also pass when the argument arrived mangled —
 * which is the failure this channel cannot afford, since the argument IS the
 * access control.
 */
export const agentPingResult = z.object({
    accountId: z.string(),
    actorPath: z.literal(AGENT_ACTOR_PATH),
    ok: z.literal(true),
    subjectAccountId: z.string().nullable(),
});
/**
 * Whether the ACTOR has opted out of product analytics (`D238`, `D239`).
 *
 * `apps/ai` asks before it emits `trainer_turn`, because the flag lives on
 * the IAM account row and a Durable Object holds no IAM database. Read for
 * the actor — the person whose turn it is — and never for a stamped subject:
 * the event is the actor's use of the trainer.
 */
export const agentAnalyticsOptOutResult = z.object({
    optedOut: z.boolean(),
});
/* ────────────────────────────────────────────────────────────────────────────
 * THE READ SURFACE (`D72`)
 *
 * Eight tools — `queryTraining`, `getProfile`, `listMeasurements`,
 * `listBodyAssessments`, `listStudents`, `findExercises`, `getPlan`,
 * `findEquipment` — each a request
 * schema here and a thin RPC method in `apps/api/src/rpc/agent-entrypoint.ts`,
 * plus one read that is NOT a tool: `getOnboardingProgress`, which the agent's own code
 * calls to decide the next `ask` (`D112`, `D113`) and no model ever sees.
 * Read the rule above once more before adding another, because it is the only
 * one that cannot be re-derived from the code:
 *
 *   ⚠️ NO REQUEST SCHEMA BELOW HAS AN ACCOUNT FIELD, AND NONE MAY GROW ONE.
 *
 * These schemas ARE what the model sees — they become the tool's JSON Schema
 * and are the entire vocabulary it may write in. The account scope arrives as
 * the separate `AgentActor` first parameter, from the stamped scope, and a
 * field here that could name a subject would hand the tenant filter back to a
 * probability distribution. If a method ever appears to need one, the scope
 * stamping is what is wrong; fix that, not the signature.
 *
 * Every one is a `strictObject`, deliberately rather than by habit. A stripping
 * object would silently DISCARD an `accountId` the model invented, which is
 * safe but teaches nothing; strict refuses the call and the refusal names the
 * field, so an injected instruction that tried it leaves a trace instead of a
 * successful-looking read. That is the "extra field" route the design's own verify
 * step asks to be closed.
 *
 * ── AND NOTHING HERE IS SQL, A TABLE, A COLUMN OR AN ORDERING ───────────────
 * `metric`, `groupBy` and `kind` are FILTER VALUES from closed sets (`kind` is
 * open in shape but bounded in length, because `D68` deliberately left the
 * measurement vocabulary open). Ordering is decided by hand-written code on the
 * far side and is not expressible from here at all. There is no route from any
 * field below to an identifier in a statement.
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * What a training question can ask FOR. `D72`'s "metric × exercises × period ×
 * grouping", first axis.
 *
 * Closed, and small on purpose: the dynamism wanted here is over QUESTIONS, not
 * over SQL, and a filter specification with these axes answers essentially
 * every question a trainer asks about a student. If one genuinely does not fit,
 * that is a signal about the schema, not an argument for a free-text field.
 */
export const TRAINING_METRICS = [
    "max_load",
    "reps",
    "sessions",
    "sets",
    "volume",
];
/**
 * The fourth axis. `total` collapses the period into one number, which is what
 * a question like *"how much did they squat last month"* wants; `day`, `week`
 * and `month` are the shape of a trend; `exercise` is the shape of the
 * question *"what are they actually doing"*.
 *
 * `week` buckets on the ISO week's MONDAY, in UTC, and the far side labels the
 * bucket with that date rather than a week number — a bare `2026-W35` is a
 * string a model reads back to a person as if it were a date.
 */
export const TRAINING_GROUPINGS = [
    "day",
    "exercise",
    "month",
    "total",
    "week",
];
/**
 * The mandatory `LIMIT`, and the two bounds that stand behind it.
 *
 * A 30-second D1 query cannot be cancelled and an `AbortSignal` on the model
 * call does nothing to a query already in flight, so the ceiling on what one
 * tool call can ask for is not a nicety. What is honest about where it applies:
 *
 *  - `MAX_PERIOD_DAYS` is the bound on ROWS TOUCHED. The period is required on
 *    every windowed read, so the indexed range scan is always closed at both
 *    ends. This is the one that protects the database.
 *  - `MAX_QUERY_LIMIT` / `MAX_MEASUREMENT_LIMIT` bound the ANSWER — how much
 *    reaches the model, and therefore the context and the bill.
 *  - `MAX_EXERCISE_IDS`, `MAX_STUDENT_ROWS` and `MAX_INACTIVITY_PROBES` bound
 *    the number of SUBREQUESTS one call can turn into. A Worker invocation caps
 *    at 1,000 D1 reads and D1 executes sequentially per database, so an
 *    unbounded fan-out over a roster is head-of-line blocking for every other
 *    user — a latency cost paid by other people, which is the argument in
 *    `reference/agent-data-access.md` §3 that actually carries.
 */
export const MAX_PERIOD_DAYS = 366;
export const MAX_QUERY_LIMIT = 200;
export const MAX_MEASUREMENT_LIMIT = 500;
export const MAX_EXERCISE_IDS = 20;
export const MAX_MEASUREMENT_KIND_LENGTH = 64;
export const MAX_STUDENT_ROWS = 200;
export const MAX_INACTIVITY_PROBES = 100;
export const MAX_MANAGED_GROUPS = 20;
export const MAX_INACTIVE_SINCE_DAYS = 365;
/**
 * The kinds queried when a measurement call names none (`D68`).
 *
 * NOT a default written into the schema. A default `kind` would answer a
 * different question from the one asked and look like the right answer;
 * querying the known series and returning each of them separately answers
 * exactly *"what do we have"*. The circumference series (`circumference:<site>`)
 * is open-ended and undiscoverable by construction, so reaching one still needs
 * an explicit `kind`.
 */
export const DEFAULT_MEASUREMENT_KINDS = ["body_fat", "weight"];
/**
 * A calendar day, `YYYY-MM-DD`, and NOT a timestamp.
 *
 * A day is what a person asks in — *"the last three months"* — and it is what a
 * model produces reliably; an instant invites a timezone the model has to guess
 * at. The far side widens `from` to 00:00:00Z and `to` to 23:59:59.999Z, so
 * both ends are inclusive and `from === to` is one whole day rather than an
 * empty window.
 */
const calendarDay = z.iso.date();
/**
 * `queryTraining` — the execution log, aggregated (`D72`).
 *
 * `from`/`to` and `limit` are REQUIRED and have no default. A default limit is
 * a limit a caller can forget about; required, the mandatory bound is a
 * property of the schema rather than of the implementation that happens to
 * apply it.
 *
 * `exerciseIds` are catalogue ids — opaque values compared against a column,
 * never anything that becomes part of a statement's text. Unknown ids match
 * nothing, which is the correct answer to a question about an exercise the
 * person has never done.
 */
export const queryTrainingRequest = z.strictObject({
    exerciseIds: z
        .array(z.string().min(1).max(64))
        .max(MAX_EXERCISE_IDS)
        .optional(),
    from: calendarDay,
    groupBy: z.enum(TRAINING_GROUPINGS).optional(),
    limit: z.int().min(1).max(MAX_QUERY_LIMIT),
    metric: z.enum(TRAINING_METRICS),
    to: calendarDay,
});
/**
 * One row of the answer. `bucket` is a LABEL, not an identifier to pass back:
 * nothing on this surface accepts one.
 *
 * `sessions` and `sets` ride along with every metric because they are what
 * makes a number readable — 4,200 kg of volume over two sessions and over
 * twelve are different facts, and a model given only the total will narrate
 * the first as if it were the second.
 */
export const trainingBucket = z.object({
    bucket: z.string(),
    sessions: z.number(),
    sets: z.number(),
    value: z.number(),
});
export const agentTrainingQueryResult = z.object({
    buckets: z.array(trainingBucket),
    from: z.string(),
    groupBy: z.enum(TRAINING_GROUPINGS),
    metric: z.enum(TRAINING_METRICS),
    /** Sets the aggregation ran over — the observable behind `D72`'s per-turn
     *  budget, and the honest one available from behind a use case. See the note
     *  on `rows_read` in `apps/api/src/rpc/agent-entrypoint.ts`. */
    setsScanned: z.number(),
    to: z.string(),
    /** Buckets existed beyond `limit` and were dropped. Present so the model can
     *  say "and more" instead of narrating a truncated list as the whole. */
    truncated: z.boolean(),
    /** `kg`, `reps`, `sets`, `sessions`, `kg·reps` — the unit of `value`, so the
     *  model does not have to infer it from the metric name and get it wrong. */
    unit: z.string(),
});
/**
 * `getProfile` — the stable half of a person (`D68`).
 *
 * EMPTY, and strict, which is the whole of its security value: a tool with no
 * parameters cannot be pointed at anybody. It is not `z.undefined()` because a
 * tool call is an object, and it is not a stripping object because `{}` that
 * quietly swallows `{ accountId }` teaches the model that the field exists.
 */
export const getProfileRequest = z.strictObject({});
/**
 * The seven lifestyle facts (`D148`) and the motivation (`D129`), as
 * {@link updateProfile} writes them and {@link getProfile} reads them back.
 *
 * Every one is `null` when the person has not answered — and `optional()` on
 * the RESULT only, for the reason the wire contract gives every new output
 * field: the two Workers deploy seconds apart, the agent first (`T-113`), and
 * an `apps/ai` that required a field `apps/api` had not started sending would
 * fail every `getProfile` parse in that window. `apps/api` always sends them.
 *
 * The vocabularies are imported from `../training/profile` rather than
 * restated: both files live in this package, so the agent and the settings
 * screen cannot disagree about what an occupation is called.
 */
const sleepHoursField = z.number().min(4).max(10).multipleOf(0.5);
const lifestyleShape = {
    motivation: z.string().max(MAX_MOTIVATION_LENGTH).nullable(),
    occupation: z.enum(OCCUPATIONS).nullable(),
    previousModalities: z.array(z.enum(PREVIOUS_MODALITIES)).max(10).nullable(),
    proteinMeals: z.enum(PROTEIN_MEALS).nullable(),
    proteinSupplement: z.enum(PROTEIN_SUPPLEMENTS).nullable(),
    sleepHoursWeekday: sleepHoursField.nullable(),
    sleepHoursWeekend: sleepHoursField.nullable(),
    /** 1 (poor) to 5 (good) — habitual, not last night's. */
    sleepQuality: z.int().min(1).max(5).nullable(),
};
export const agentProfileResult = z.object({
    /** `null` when the person has never filled one in — a fact about the account,
     *  not an error, and the model should say so rather than guess an age. */
    profile: z
        .object({
        accountId: z.string(),
        /** Derived from the date of birth at read time and never stored (`D68`) —
         *  an age column goes stale within a year while still looking plausible. */
        age: z.number(),
        dateOfBirth: z.string(),
        heightCm: z.number().nullable(),
        motivation: lifestyleShape.motivation.optional(),
        occupation: lifestyleShape.occupation.optional(),
        previousModalities: lifestyleShape.previousModalities.optional(),
        proteinMeals: lifestyleShape.proteinMeals.optional(),
        proteinSupplement: lifestyleShape.proteinSupplement.optional(),
        sex: z.string(),
        sleepHoursWeekday: lifestyleShape.sleepHoursWeekday.optional(),
        sleepHoursWeekend: lifestyleShape.sleepHoursWeekend.optional(),
        sleepQuality: lifestyleShape.sleepQuality.optional(),
        updatedAt: z.string(),
    })
        .nullable(),
});
/**
 * `getOnboardingProgress` — where the person is in Session 1 (`D113` · `D114`).
 *
 * Empty and strict for the same reason `getProfileRequest` is: there is
 * nothing to parameterise, and a request that quietly swallowed an
 * `{ accountId }` would teach the caller the field exists. It is parsed even
 * though the model never calls this method — the agent's own code does — so
 * that the two Workers' ideas of the call agree at runtime, not just in a
 * type both bundles compiled separately (`T-35`).
 */
export const getOnboardingProgressRequest = z.strictObject({});
/** `getTurnContext` (`D211`) — nothing to parameterise, like the onboarding
 *  read beside it. */
export const getTurnContextRequest = z.strictObject({});
/**
 * THE SAME SCHEMA the app reads over `GET /me/onboarding`, not a restatement.
 * Both live in this package, so the reuse is an import rather than a drift
 * guard: the agent and the app cannot disagree about what "where are they in
 * the script" means, because there is one definition of it (`D113`).
 */
export const agentOnboardingProgressResult = onboardingProgress;
/**
 * `listMeasurements` — the series (`D68`).
 *
 * `kind` is optional and its absence means "the known kinds", not a default —
 * see {@link DEFAULT_MEASUREMENT_KINDS}. Weight is here and not in the profile
 * because the CHANGE is the data.
 */
export const listMeasurementsRequest = z.strictObject({
    from: calendarDay,
    kind: z.string().min(1).max(MAX_MEASUREMENT_KIND_LENGTH).optional(),
    limit: z.int().min(1).max(MAX_MEASUREMENT_LIMIT),
    to: calendarDay,
});
export const agentMeasurementRow = z.object({
    /** `direct` or `agent` (`D69`) — *"it was the agent, at your trainer's
     *  request"* is a different answer from *"it was your trainer"*. */
    actorPath: z.string(),
    measuredAt: z.string(),
    measurementId: z.string(),
    unit: z.string(),
    value: z.number(),
});
export const agentMeasurementSeries = z.object({
    kind: z.string(),
    /** The most recent row IN THE WINDOW, before truncation. Truncating keeps the
     *  most recent rows, so this is normally `rows.at(-1)` — it is carried
     *  separately so that stays true even when it is not. */
    latest: agentMeasurementRow.nullable(),
    rows: z.array(agentMeasurementRow),
    truncated: z.boolean(),
});
export const agentMeasurementsResult = z.object({
    from: z.string(),
    series: z.array(agentMeasurementSeries),
    to: z.string(),
});
/**
 * `listBodyAssessments` — the photo assessment's NUMBERS (`D25`).
 *
 * ── THE DECISION THIS SCHEMA EXISTS TO HOLD ─────────────────────────────────
 * **The trainer reads the numbers and never the photographs.** So this shape
 * carries a ratio, a percentage with its range, a verdict and a list of warning
 * codes — and it carries no photo key, no photo, no `features` block and none
 * of `bodycomp`'s LLM NOTES, which are sentences about what the photographs
 * show (*"the shorts are loose around the waist"*) and are therefore the
 * photographs restated in words. `apps/api/src/rpc/agent-entrypoint.ts` is the
 * only composition root of the three that could reach a photo store, and `T96`
 * deliberately left it without one; this schema is the second half of that —
 * there is no field here for a byte to arrive in.
 *
 * ── AND WHY IT IS NOT A `kind` ON {@link listMeasurementsRequest} ────────────
 * `D23`: a photo body fat and a typed one differ SYSTEMATICALLY, so merging the
 * two series would produce a trend whose steps are method changes. They are two
 * answers to the same question and a model shown one list would narrate it as
 * one measurement repeated.
 *
 * ── NO PERIOD, WHICH IS THE ONE DEPARTURE FROM THE READS ABOVE ──────────────
 * Every windowed read here takes `from`/`to` because the period is what closes
 * the range scan. This read is not windowed: the far side asks for the newest
 * rows of one account through `idx_body_assessment_account_created` under a
 * constant cap the caller cannot move (`BODY_ASSESSMENT_LIST_LIMIT`, 200), so
 * the bound on rows touched is a property of the code rather than of the
 * request. `limit` therefore bounds only the ANSWER, which is why it is the one
 * limit on this surface that may be omitted — absence means *"as many as this
 * schema allows"*, never an unbounded read.
 */
export const MAX_BODY_ASSESSMENT_ROWS = 20;
export const listBodyAssessmentsRequest = z.strictObject({
    /** Newest first. Omitted means {@link MAX_BODY_ASSESSMENT_ROWS} — see above. */
    limit: z.int().min(1).max(MAX_BODY_ASSESSMENT_ROWS).optional(),
});
/**
 * One settled assessment. `done` rows only, so every field is present: the
 * aggregate writes the ratio, the percentage, the verdict and the completion
 * stamp in ONE transition ({@link BodyAssessment.settle}), and a `pending` or
 * `failed` row has none of them. Nullable fields here would describe a state
 * this read does not return and would invite a model to narrate `null` as zero.
 */
export const agentBodyAssessmentRow = z.object({
    /**
     * An ESTIMATE with a ~95% range, and the range is why `low` and `high` are
     * not optional: the interval is 12–15 pp wide unless a personal reference
     * narrowed it, and a bare percentage reads as a measurement it is not.
     * `rangeModel` says which of the two it is — `calibrated` is the population
     * interval, `anchor` the narrower one a DXA or bioimpedance reading earned.
     */
    bodyFat: z.object({
        high: z.number(),
        low: z.number(),
        percent: z.number(),
        rangeModel: z.enum(BODY_FAT_RANGE_MODELS),
    }),
    /**
     * `false` means the photo protocol failed — loose clothing, shoes on, hair
     * over the outline, arms against the trunk — and this number may not be
     * compared with any other. It is still the person's measurement, which is why
     * the row is returned rather than hidden (`list-body-assessments.ts`).
     */
    comparable: z.boolean(),
    completedAt: z.string(),
    id: z.string(),
    /**
     * {@link BODYCOMP_WARNING_CODES} today — bare strings, because the list
     * belongs to a service deployed on its own clock and an unknown code must not
     * refuse the whole answer. The `fields` and `spreadPp` a warning carries are
     * deliberately NOT here: both describe the photographs.
     */
    warningCodes: z.array(z.string()),
    /**
     * The PRIMARY number: waist ÷ height with its NICE band, ±0.018 against the
     * body fat's ±6 pp. `nearThreshold` is true within 0.02 of a band edge, and
     * then the band is a boundary rather than a verdict.
     */
    whtr: z.object({
        band: z.enum(BODY_WHTR_BANDS),
        nearThreshold: z.boolean(),
        value: z.number(),
    }),
});
export const agentBodyAssessmentsResult = z.object({
    /** Newest first. Empty for somebody who has never taken one, which is the
     *  ordinary first answer and not a refusal. */
    items: z.array(agentBodyAssessmentRow),
});
/**
 * `listStudents` — the trainer's portfolio (`D50`, `D64`).
 *
 * `inactiveSinceDays` is the one question this list is actually asked: *"who
 * needs attention"*. Omitted, it lists everybody.
 *
 * There is no `groupId` parameter, and its absence is the design rather than an
 * omission: the list is ONE list across every group the caller manages, with
 * each row marked by the group it came through. A trainer employed by a gym who
 * also has their own clients has different obligations to each, and a merged
 * list that does not say which is how they act on the wrong one.
 */
export const listStudentsRequest = z.strictObject({
    inactiveSinceDays: z.int().min(1).max(MAX_INACTIVE_SINCE_DAYS).optional(),
});
export const agentStudentRow = z.object({
    /**
     * The student's account id.
     *
     * ⚠️ INERT BY CONSTRUCTION, and it must stay that way. Nothing on this
     * surface ACCEPTS an account id, so this value cannot be passed back to point
     * a later call at somebody — it is a label the model may use to tell two
     * students apart within one answer, and the reach it names was already
     * granted by the membership that put the row here. The day a tool takes one
     * as a parameter, this field becomes the injection payload.
     */
    accountId: z.string(),
    groupId: z.string(),
    groupName: z.string(),
    /** When the student accepted (`D50`) — `null` while still pending. */
    memberSince: z.string().nullable(),
    /** The CALLER's role in that group, not the student's: `owner`, `admin` or
     *  `trainer`. This is the mark that carries the obligation. */
    viewerGroupRole: z.string(),
});
export const agentStudentsResult = z.object({
    /** Echoed so an answer cannot silently claim the roster is small when it was
     *  filtered. `null` when no filter was asked for. */
    inactiveSinceDays: z.number().nullable(),
    students: z.array(agentStudentRow),
    /** A student in two of the caller's groups appears TWICE, once per group —
     *  see the note on `listStudentsRequest`. */
    truncated: z.boolean(),
});
/* ────────────────────────────────────────────────────────────────────────────
 * TWO MORE READS, ADDED BECAUSE THE WRITES CANNOT BE CALLED WITHOUT
 * THEM (the second was flagged by name)
 *
 * `upsertPlanItems` and `recordWorkoutSet` both take a CATALOGUE EXERCISE ID,
 * and before this pair there was no way for a model to obtain one: the four
 * tools return an exercise id only as a `groupBy: "exercise"` bucket label, so
 * a person could be prescribed nothing they had not already done. A write
 * surface whose arguments are unobtainable is not a shortcut (`D73`), it is a
 * tool the model calls wrongly and then apologises for.
 *
 * `getPlan` is the one flagged when the read surface was designed — and
 * `upsertPlanItems` REPLACES the item list, so without it the model would be
 * rewriting a plan it cannot see.
 *
 * The rule above holds unchanged for both: no account field, strict object.
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * `D52` keeps catalogue administration out of scope entirely, and the
 * `catalog_` prefix marks an immutable seed. Nothing on this surface writes a `catalog_*` table — there is no
 * method for it and no use case behind one — so the catalogue is READ-ONLY to
 * every tool by construction rather than by a guard that could be forgotten.
 *
 * The two axes a model actually asks along: a name to search for, and the
 * equipment or region it may use. Everything else the list screen filters by
 * is deliberately absent — this is the lookup that turns *"barbell squat"* into
 * an id, not a second exercise browser.
 */
export const MAX_EXERCISE_SEARCH_LIMIT = 25;
export const MAX_SEARCH_LENGTH = 64;
/** `en` and `pt` are the catalogue's translated languages. `en` when omitted;
 *  an unknown code is refused rather than silently answered in English. */
export const CATALOGUE_LOCALES = ["en", "pt"];
export const findExercisesRequest = z.strictObject({
    limit: z.int().min(1).max(MAX_EXERCISE_SEARCH_LIMIT),
    locale: z.enum(CATALOGUE_LOCALES).optional(),
    /** Free text, matched by the read model's own fold. It reaches a BOUND
     *  parameter and never a statement's text, and it is bounded in
     *  length because a leading-wildcard `LIKE` over an unbounded string is the
     *  one shape `T-6` forbids outright. */
    search: z.string().min(1).max(MAX_SEARCH_LENGTH).optional(),
});
export const agentExerciseRow = z.object({
    category: z.string(),
    equipment: z.string(),
    /**
     * The id `upsertPlanItems` and `recordWorkoutSet` take.
     *
     * Unlike `agentStudentRow.accountId` this one IS meant to be passed back —
     * and it is safe to be, because it names an EXERCISE and not a person. It
     * cannot widen what the conversation may speak about; the account is still
     * the actor's, still injected, still absent from every schema.
     */
    exerciseId: z.string(),
    name: z.string(),
    primaryMuscle: z.string(),
    /**
     * Whether the person's saved equipment covers this exercise (`D211`) —
     * absent when they never said what they have. On 2026-10-03 the trainer,
     * asked for "um exercício aleatório", added a barbell bench press and a
     * barbell squat for a person who owns dumbbells only: nothing it read said
     * which rows they could do.
     */
    availableToPerson: z.boolean().optional(),
});
export const agentExerciseSearchResult = z.object({
    exercises: z.array(agentExerciseRow),
    /** More matched than were returned — so the model can narrow the search
     *  instead of narrating the first page as the whole catalogue. */
    truncated: z.boolean(),
});
/**
 * Which of the person's two plans a plan tool reads or writes (`D191`): the
 * one in effect, or the draft waiting for review.
 *
 * ── A WORD, NEVER AN ID ─────────────────────────────────────────────────────
 * A `planId` parameter would let a trainer's conversation stamped for one
 * student name another student's plan, because `requirePlanWriteAccess`
 * checks the ACTOR's reach and not the conversation's subject. So the model
 * names WHICH plan and the far side resolves its id from the subject — the
 * active plan, or the draft `GET /me/plan?draft=1` would open. Item and day
 * ids are then validated INSIDE that plan by the aggregate, so an id from
 * anybody else's plan is `plan.item_not_found`, never a write.
 */
export const AGENT_PLAN_SCOPES = ["active", "draft"];
export const agentPlanScope = z
    .enum(AGENT_PLAN_SCOPES)
    .describe("Which plan: `active` (the one in effect, the default) or `draft` (the proposal waiting for the person to accept).");
/**
 * `getPlan` — the person's plan, as the app shows it (`D51`, `D191`).
 *
 * No `planId` — see {@link agentPlanScope}. `locale` names the language the
 * exercise names come back in.
 */
export const getPlanRequest = z.strictObject({
    locale: z.enum(CATALOGUE_LOCALES).optional(),
    plan: agentPlanScope.optional(),
});
/**
 * One line of the plan (`D191`). `itemId` and `sessionId` are ADDRESSES the
 * plan tools take back — validated inside the subject's own plan on the far
 * side, never a way to reach anybody else's.
 */
export const agentPlanItem = z.object({
    exerciseId: z.string(),
    /** The catalogue's name in the requested language, or `null` for an id the
     *  catalogue no longer resolves. */
    exerciseName: z.string().nullable().optional(),
    itemId: z.string().optional(),
    notes: z.string().nullable(),
    /** One-based, in the order the day reads. */
    position: z.number(),
    /** The engine's reason for the line (`D147`) — an i18n key, or `null` for
     *  a line placed by hand. */
    reasonKey: z.string().nullable().optional(),
    restSeconds: z.number().nullable().optional(),
    /** The day this line is in — a `sessionId` of `sessions`. */
    sessionId: z.string().optional(),
    /**
     * The superset the line is performed in (`D246`): lines of one day sharing
     * it are done in rounds, with the rest after the round — the longest rest
     * among them — and none between them. `null` for a line on its own.
     */
    supersetId: z.string().nullable().optional(),
    targetDurationSeconds: z.number().nullable().optional(),
    targetLoad: z.number().nullable(),
    targetReps: z.number().nullable(),
    targetRepsMax: z.number().nullable().optional(),
    targetRepsMin: z.number().nullable().optional(),
    targetSets: z.number().nullable(),
});
/** One training day of the plan (`D119`) — its items are the `items` rows
 *  carrying its `sessionId`. */
export const agentPlanSession = z.object({
    /** The engine's label — `"Full body A"`. */
    label: z.string(),
    /** What the day works — *"Peito & Dorsais"* — or `null` when none was
     *  stored. */
    name: z.string().nullable(),
    position: z.number(),
    sessionId: z.string(),
    /** 0 = Sunday … 6 = Saturday, or `null` for a day with no fixed weekday. */
    weekday: z.number().nullable(),
});
/** The engine's standing suggestion on the plan in effect (`D150`). */
export const agentPlanSuggestion = z.object({
    /** The key's params — `{ weeks }`, or the stalled `{ exerciseId }`. */
    params: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
    /** The protocol it suggests moving to, or `null` for a plateau. */
    protocolId: z.string().nullable(),
    reasonKey: z.string(),
});
/**
 * Where the plan in effect is in its weeks (`D211`) — what the app's coach
 * panel shows as "Semana N de M". On 2026-10-03 the person told the trainer
 * the chat said "semana 2 de 4"; the trainer had no way to see it, nor to
 * check that a restart had worked.
 */
export const agentPlanWeek = z.object({
    /** The block length — the protocol's deload cadence; `null` by hand. */
    blockWeeks: z.int().positive().nullable(),
    /** 1-based week since the plan went into effect (or was restarted). */
    current: z.int().positive(),
    /** Whether `current` is a deload week of the protocol. */
    deload: z.boolean(),
    /** The next deload week at or after `current`; `null` by hand. */
    nextDeloadWeek: z.int().positive().nullable(),
    /** The day week 1 started (UTC), `YYYY-MM-DD`. */
    startedOn: z.string(),
    /** `current` inside its block — the N of "Semana N de M". */
    weekOfBlock: z.int().positive().nullable(),
});
/** What a turn's context note says beside the date (`D211`). */
export const agentTurnContextResult = z.object({
    /** `null` when no plan is in effect. */
    planWeek: agentPlanWeek.nullable(),
    /**
     * The unit the ACTOR reads loads in (`D266`) — the person typing, never
     * the stamped subject, because it is their screen the answer lands on.
     * `null` is "never chose", read as kilograms. Optional so an agent built
     * after this field still parses an API deployed before it. Every tool
     * still takes and returns kilograms; this only tells the trainer which
     * unit to SPEAK in.
     */
    weightUnit: weightUnit.nullable().optional(),
});
export const agentPlanResult = z.object({
    /**
     * Whether the CALLER may write it (`D51`). Returned because a refusal with no
     * warning in front of it is a trainer composing a session's worth of changes
     * and losing them — the same argument that put `canWrite` on the use case's
     * own view.
     */
    canWrite: z.boolean(),
    /** Whether a draft is waiting for review — read it with `plan: "draft"`. */
    hasDraft: z.boolean().optional(),
    items: z.array(agentPlanItem),
    /** `null` when nobody has written one yet — the ordinary first-run state,
     *  not an error, and the model should offer to build one rather than
     *  report a fault. */
    plan: z
        .object({
        actorPath: z.string(),
        itemCount: z.number(),
        /** The protocol the plan follows, or `null` for a hand-built one. */
        protocolId: z.string().nullable().optional(),
        /** Which plan this is — the scope that was asked for. */
        scope: agentPlanScope.optional(),
        updatedAt: z.string(),
        /** Where the plan is in its weeks (`D211`); `null` for a draft. */
        week: agentPlanWeek.nullable().optional(),
    })
        .nullable(),
    /** The plan's days, in order. One implicit day for a plan with none. */
    sessions: z.array(agentPlanSession).optional(),
    /** Now, as the server reads it: an ISO instant and its UTC weekday
     *  (`D211`) — "ontem" and "segunda" resolve against this. */
    today: z
        .object({ instant: z.string(), weekdayUtc: z.int().min(0).max(6) })
        .optional(),
    suggestion: agentPlanSuggestion.nullable().optional(),
});
/**
 * `findEquipment` — the catalogue's equipment names to their ids (`D149`).
 *
 * The third read the writes cannot be called without: {@link setEquipment}
 * takes `catalog_equipment` ids, and *"I bought dumbbells"* names none. Like
 * `findExercises` it is a catalogue lookup and not a personal record — no
 * account, strict object, read-only by construction (`D52`).
 *
 * `locale` is the CONVERSATION's language, which the model knows from its own
 * prompt and the far side cannot. `en` when omitted, and a name with no
 * translation falls back to English on the far side, so the model always gets
 * a name it can read back to the person.
 */
export const MAX_EQUIPMENT_QUERY_LENGTH = 64;
export const MAX_EQUIPMENT_MATCHES = 10;
export const findEquipmentRequest = z.strictObject({
    locale: z.enum(CATALOGUE_LOCALES).optional(),
    /** A name or part of one, in the conversation's language. Folded and
     *  matched against the localized names — never a statement's text. */
    query: z.string().min(1).max(MAX_EQUIPMENT_QUERY_LENGTH),
});
export const agentEquipmentSearchResult = z.object({
    /** Exact name first, then alphabetical; at most `MAX_EQUIPMENT_MATCHES`.
     *  Empty means the catalogue has nothing by that name — say so rather than
     *  guess an id. */
    matches: z
        .array(z.object({ id: z.int().positive(), name: z.string() }))
        .max(MAX_EQUIPMENT_MATCHES),
});
/**
 * `getTrainingSetup` — the config and the equipment list as stored (`D149`).
 *
 * The read the two whole-row writes cannot be used without: `setTrainingConfig`
 * replaces every field and `setEquipment` replaces the whole list, so a model
 * told *"change my goal to strength"* has to read the days, the experience and
 * the session length it is about to restate — and the prompt forbids
 * collecting them again. Strict, like `getProfileRequest`, and for the same
 * reason: nothing here can name a person. `locale` is the one field, because
 * the equipment names come back in the conversation's language and the far
 * side cannot know it (the `findEquipment` argument).
 *
 * `config` is THE SAME SCHEMA `GET /me/training-config` answers, imported;
 * `null` when the person has never set one. `equipment` is `null` for the
 * same absence, and an EMPTY list when the row exists and says "nothing" —
 * two facts a model deciding whether to call `setEquipment` must tell apart.
 */
export const getTrainingSetupRequest = z.strictObject({
    locale: z.enum(CATALOGUE_LOCALES).optional(),
});
export const agentTrainingSetupResult = z.object({
    config: trainingConfigOutput.nullable(),
    equipment: z
        .array(z.object({ id: z.int().positive(), name: z.string() }))
        .nullable(),
    /**
     * The person's saved gyms (`D202`, `D228` extended) — `inUse` is the one
     * switched to last, whose list is the equipment above. OPTIONAL: an older
     * API answers without it, and a trainer reading a student gets none —
     * gyms are `/me/` only.
     */
    gyms: z
        .array(z.object({
        equipmentCount: z.int().nonnegative(),
        id: z.string(),
        inUse: z.boolean(),
        name: z.string(),
    }))
        .optional(),
});
/* ────────────────────────────────────────────────────────────────────────────
 * THE WRITE SURFACE (`D69` · `D73`)
 *
 * `D73`: the agent WRITES. Not proposals for a human to retype — if it produces
 * text somebody transcribes it is not a shortcut, it is an extra step.
 *
 * Everything the read rule says applies here unchanged, and one thing more:
 *
 *   ⚠️ EVERY WRITE IS ATTRIBUTED, AND `actorPath` IS NOT A PARAMETER.
 *
 * `D69` puts two columns on every written row: WHICH ACCOUNT caused the write,
 * and BY WHICH PATH. *"It was the agent, at your trainer's request"* is a
 * different answer from *"it was your trainer"*, because it means a model
 * composed the row and a human at most did not disagree. The account comes from
 * the actor; the path is {@link AGENT_ACTOR_PATH}, stamped by the runtime.
 *
 * A tool that could pass `direct` while running through the agent would make
 * the column record a PREFERENCE rather than a fact — and it is exactly the
 * column somebody will rely on in a dispute. So it appears in no schema below,
 * and the far side never reads it from a request.
 *
 * ── WHY THERE IS NO CONFIRMATION STEP ───────────────────────────────────────
 * Deliberately absent. `D73` rejected proposals in favour of direct writes, and
 * a confirmation is a proposal with a different name. What makes an exploratory
 * *"what if we swapped the squat?"* recoverable is attribution and history
 * (`D69`) plus soft delete (`D53`) — never a prompt.
 *
 * ── AND WHY A REFUSAL IS A RESULT, NOT AN EXCEPTION ─────────────────────────
 * See {@link agentWriteRefusal}.
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * A write the domain refused, shaped so the model can RELAY it.
 *
 * A use case refusing is not a crash: `D51`'s managing-group rule, `D70`'s
 * no-personal-data-in-templates and the catalogue's immutability are the
 * product's own rules, and hitting one is a thing the person needs to be told
 * — *"another group holds the pen on this plan"* — not a stack trace and a
 * turn that dies. So every write method answers with a discriminated union and
 * the tool hands the refusal back as an ordinary tool result.
 *
 * ── WHAT TRAVELS, AND WHAT DELIBERATELY DOES NOT ────────────────────────────
 * `code` and `messageKey` only. `DomainError.message` is developer-facing by
 * that class's own rule — *"deliberately allowed to be more specific than
 * anything a user should be shown; an enumeration oracle lives in exactly that
 * gap"* — and the model is a client. `params` are dropped for a second reason:
 * some carry identifiers (`session_already_open` carries a session id), and
 * nothing on this surface ACCEPTS an identifier, so keeping them out means a
 * refusal cannot become the payload for the next call.
 *
 * A failure that is NOT a domain refusal — a broken binding, a D1 outage —
 * still throws. Those are not answers, and dressing them as one would teach the
 * model to retry something that cannot succeed.
 */
export const agentWriteRefusal = z.object({
    /** `VALIDATION`, `FORBIDDEN`, `NOT_FOUND`, `CONFLICT` — `DomainError.code`. */
    code: z.string(),
    /** `<domain>.<condition>`, stable forever once shipped. The model phrases
     *  it; it is not a sentence to read out. */
    messageKey: z.string(),
    ok: z.literal(false),
});
/**
 * The bounds a write may not exceed. Every one is a REFUSAL at the schema, so
 * an absurd value never reaches a use case and never becomes a row somebody has
 * to find later.
 *
 * `MAX_PLAN_ITEMS` also bounds subrequests: `upsertPlanItems` is one call per
 * item removed plus one per item written, and a Worker invocation caps at 1,000
 * D1 reads with D1 executing sequentially per database.
 */
export const MAX_PLAN_ITEMS = 40;
export const MAX_NOTE_LENGTH = 500;
export const MAX_MEASUREMENT_VALUE = 1000;
export const MAX_UNIT_LENGTH = 8;
export const MAX_REPS = 1000;
export const MAX_LOAD = 2000;
export const MAX_TARGET_SETS = 100;
/** An hour under load is already the longest plank anybody holds (`D151`). */
export const MAX_SET_DURATION_SECONDS = 3600;
/** Ten kilometres — past that it is a run somebody logged as a carry (`D151`). */
export const MAX_SET_DISTANCE_METRES = 10_000;
/**
 * `recordMeasurement` — one point appended to the series (`D68`, `D69`).
 *
 * It APPENDS; nothing here overwrites. Two weigh-ins on two days are two rows
 * and both survive, because the change IS the data.
 *
 * `unit` travels with the value and nothing converts it — the same `kind` is
 * kilograms for one person and pounds for another, and a series that silently
 * mixes them is worse than no series. So the model must state it, and a value
 * with the wrong unit is a visible wrong row rather than an invisible one.
 */
export const recordMeasurementRequest = z.strictObject({
    /** `weight`, `body_fat`, `circumference:<site>` — open-ended (`D68`). */
    kind: z.string().min(1).max(MAX_MEASUREMENT_KIND_LENGTH),
    /** The day the BODY was measured, not the day of this call. Omitted means
     *  now — stepping off the scale and saying so. See the note on
     *  `calendarDay`: a day is what a person says and what a model produces
     *  reliably; an instant invites a timezone it would have to guess. */
    measuredAt: calendarDay.optional(),
    /** `kg`, `lb`, `%`, `cm`. */
    unit: z.string().min(1).max(MAX_UNIT_LENGTH),
    value: z.number().positive().max(MAX_MEASUREMENT_VALUE),
});
export const agentMeasurementWriteResult = z.discriminatedUnion("ok", [
    z.object({
        kind: z.string(),
        measuredAt: z.string(),
        measurementId: z.string(),
        ok: z.literal(true),
        unit: z.string(),
        value: z.number(),
    }),
    agentWriteRefusal,
]);
/**
 * One prescribed exercise. Every target is optional, and that is the design: a
 * prescription of *"bench press, three sets, load up to you on the day"* is a
 * real one, and a zero forced into `targetLoad` would make it indistinguishable
 * from an instruction to lift nothing.
 */
export const agentPlanItemInput = z.strictObject({
    /** A catalogue id, from {@link findExercisesRequest}. A foreign key into
     *  `catalog_exercise` — an id the catalogue does not have is refused. */
    exerciseId: z.string().min(1).max(64),
    notes: z.string().max(MAX_NOTE_LENGTH).nullable().optional(),
    targetLoad: z.number().min(0).max(MAX_LOAD).nullable().optional(),
    targetReps: z.int().min(1).max(MAX_REPS).nullable().optional(),
    targetSets: z.int().min(1).max(MAX_TARGET_SETS).nullable().optional(),
});
/**
 * `upsertPlanItems` — the plan in effect, rewritten (`D51`, `D73`).
 *
 * ── IT IS A REPLACEMENT, AND THAT IS WHY THERE IS A `getPlan` ───────────────
 * `items` is the plan's whole item list, in order. Anything absent from it is
 * removed. The alternative — an append-only tool — cannot express *"drop the
 * leg press"*, which is half of what a trainer does, and a diff keyed by item
 * id would mean handing the model item ids to pass back.
 *
 * Removal is a SOFT delete (`D53`), so a replacement is recoverable and
 * The change feed shows every part of it. Read the plan first.
 *
 * ── AND THERE IS NO `planId` ────────────────────────────────────────────────
 * One active plan per person (`D51`). The far side resolves it from the actor's
 * subject, and creates one — self-directed, or designated to the single group
 * through which the caller reaches this person — when there is none.
 */
export const upsertPlanItemsRequest = z.strictObject({
    items: z.array(agentPlanItemInput).min(1).max(MAX_PLAN_ITEMS),
});
export const agentPlanWriteResult = z.discriminatedUnion("ok", [
    z.object({
        /** True when this call wrote the person's first plan and put it in
         *  effect, so the model can say so rather than implying one was edited. */
        created: z.boolean(),
        itemsRemoved: z.number(),
        itemsWritten: z.number(),
        ok: z.literal(true),
    }),
    agentWriteRefusal,
]);
/**
 * `proposeProtocol` — record which protocol the agent PROPOSES for the plan in
 * effect (`D102`). It never applies: `protocol_id` is untouched and the id
 * lands in `proposed_protocol_id`, where it waits for a yes.
 *
 * The id is from the protocol catalogue (`D118`) and the far side refuses one
 * the catalogue does not have. There is no `planId` — one active plan per
 * person (`D51`), resolved from the actor's subject like `upsertPlanItems`.
 */
export const proposeProtocolRequest = z.strictObject({
    /** An id from the protocol catalogue — `D118`'s frozen constant. */
    protocolId: z.string().min(1).max(64),
});
/**
 * `acceptProtocol` — move the STANDING proposal into `protocol_id` (`D102`).
 *
 * No parameters at all, like {@link getPlanRequest}: what is applied is the
 * proposal already on the plan, so there is no id the model could substitute.
 * With nothing proposed it is refused — an acceptance needs something to
 * accept. The tool's description carries the human-consent guard; this schema
 * carries the structural half.
 */
export const acceptProtocolRequest = z.strictObject({});
export const agentProtocolProposalResult = z.discriminatedUnion("ok", [
    z.object({
        /** `false` from `proposeProtocol` — recorded, waiting for a yes. `true`
         *  from `acceptProtocol` — the plan now follows it. */
        applied: z.boolean(),
        ok: z.literal(true),
        protocolId: z.string(),
    }),
    agentWriteRefusal,
]);
/* ────────────────────────────────────────────────────────────────────────────
 * THE PROPOSAL DOOR (`D102` · `D106` · `D150`)
 *
 * `proposePlanDraft` is the one write on this surface whose result is meant to
 * be LOOKED AT before it is followed. Everything else here takes effect the
 * moment the far side returns; this writes a plan the person is not yet on.
 *
 * ── IT PROPOSES, IT NEVER PUBLISHES ─────────────────────────────────────────
 * `TrainingPlan.draft` creates `status: "archived"` on purpose, so the person
 * keeps following the incumbent while they review it
 * (`propose-plan.ts`), and `publish()` is the accept button's job. There is
 * deliberately no `publishPlan` tool beside this one, and adding one would be
 * the act `ENGINEERING-BACKLOG.md` rules out in as many words: *"adding
 * exercises to a plan the person did not review is the same act as switching
 * protocols and goes through the same proposal door."*
 *
 * ── WHAT COMES BACK IS AN ADDRESS, NOT A PLAN ───────────────────────────────
 * `draftId` and the draft's sessions, because that is exactly what the
 * `openScreen` handoff needs to send the person to the editor — and because
 * `T-145`/`D150` make the alternative a silent bug: `/plan/review` generates
 * on mount from its params, so a handoff built out of GENERATE PARAMETERS
 * throws away the draft this call just wrote and produces a different one, two
 * 200s and a valid deck and the wrong plan. The id is what travels.
 *
 * The item list is NOT returned. The model does not need it to say what it
 * built — `sessionCount` and the protocol are the sentence — and the editor
 * loads the plan itself. A hundred exercises through the model's context to
 * produce one sentence is `T-6` pointed at a token bill.
 *
 * ── AND THE PROTOCOL COMES BACK AS AN ID PLUS ITS EVIDENCE, NOT A NAME ──────
 * `ProtocolDefinition` carries a `nameKey` and no name (`D101`, `D118`): the
 * display name lives in the app's locale files, and a server that invented an
 * English one would put it in the middle of a pt-BR turn (`D43`). So the
 * protocol's IDENTITY travels and the model writes the sentence.
 *
 * `evidenceIds` travels with it because `D19` and AGENT-RULES forbid stating a
 * finding without an identifier, and every number this protocol applies — sets,
 * reps, rest, the deload — is somebody's finding. Without the ids beside the
 * proposal, a model that describes what it just built either cites nothing and
 * is rejected by the validator, or goes looking through the corpus for the
 * record that happens to match. These are the protocol's own.
 * ──────────────────────────────────────────────────────────────────────────── */
/** A week has at most six training days (`training-config.ts`'s `daysPerWeek`),
 *  so a draft has at most six sessions. A bound, not an expectation. */
export const MAX_DRAFT_SESSIONS = 6;
/**
 * `proposePlanDraft` — run the engine and persist the result as a DRAFT.
 *
 * ── THERE IS NO `subjectAccountId`, AND NO `planId` EITHER ──────────────────
 * The rule the whole file holds: the account is the `AgentActor` first
 * parameter, from the stamped scope. And there is no plan id because a
 * proposal CREATES the plan — the only identifier here is a protocol, which
 * names a row of a frozen catalogue and cannot name a person.
 *
 * ── `protocolId` IS OPTIONAL, AND OMITTING IT IS THE COMMON CASE ────────────
 * Omitted, the far side keeps the incumbent plan's protocol and falls back to
 * the engine's default (`D103`) — the same resolution `POST /me/plan/generate`
 * performs, so a proposal made through the trainer and one made from the app
 * produce the same plan for the same person. Sent, it is an id from the
 * protocol catalogue (`D118`) and one the catalogue does not have is refused.
 *
 * ── AND THERE IS NO EXCLUSION LIST HERE, DELIBERATELY ───────────────────────
 * `POST /me/plan/generate` takes `excludeExerciseIds` for `D150`'s plateau
 * flow, where the app knows which lift stalled. The model does not, and a
 * free list of exercises to keep out is a way for a conversation to shape a
 * prescription the engine is supposed to own (`D102`: selection stays the
 * engine's, the LLM only explains). The person's standing refusals (`D06`)
 * are read by the use case itself on every proposal, so what the person
 * actually excluded is honoured without the model naming anything. (`D191`
 * later added the per-request list back, for the plateau's one lift.)
 *
 * ── THE WEEK'S SHAPE IS NOW THE PERSON'S (`D213`, amending `D158`) ─────────
 * `split`, a custom week's `days` and `priorityMuscles` say what the week is
 * shaped as, never which exercise goes in it: the engine still selects every
 * one. `ramos-campo-2024-split` found split and full-body routines
 * equivalent, so the shape is a matter of schedule and preference, and a
 * shape the engine cannot build is refused rather than built short.
 */
export const proposePlanDraftRequest = z
    .strictObject({
    /**
     * The days of a `custom` week, in order (`D213`): each a label in the
     * person's language and the movement patterns it trains. Only with
     * `split: "custom"`.
     */
    days: z.array(planDayInput).min(2).max(MAX_DRAFT_SESSIONS).optional(),
    /** Catalogue exercise ids the engine must leave out of THIS proposal —
     *  the plateau suggestion's stalled lift (`D150`, `D191`). A standing
     *  refusal is `excludeExercise`, not this. */
    excludeExerciseIds: z.array(z.string().min(1).max(64)).max(20).optional(),
    /** Muscles to bias the week towards (`D213`), at most three. */
    priorityMuscles: z
        .array(priorityMuscle)
        .max(MAX_PRIORITY_MUSCLES)
        .optional(),
    /** An id from the protocol catalogue — `D118`'s frozen constant. Omitted
     *  keeps the incumbent's protocol, then the engine's default (`D103`). */
    protocolId: z.string().min(1).max(64).optional(),
    /**
     * The week's shape the person agreed (`D213`, amending `D158`). Omitted,
     * the protocol's own split applies: 2–3 days full body, 4 upper/lower,
     * 5–6 push/pull/legs for the beginner protocols, upper/lower for the
     * intermediate ones.
     */
    split: planSplit.optional(),
})
    .superRefine(requireDaysWithCustom);
/** `listProtocols` takes nothing: the catalogue is the same for everyone. */
export const listProtocolsRequest = z.strictObject({});
/** One day of a week shape: the engine's label and its patterns. */
export const agentProtocolDay = z.object({
    focus: z.array(movementPattern),
    label: z.string(),
});
/** What a week of N days becomes under a split (`D213`). */
export const agentProtocolWeek = z.object({
    days: z.array(agentProtocolDay),
    daysPerWeek: z.int(),
    split: planSplit,
});
export const agentProtocolListing = z.object({
    /** The movement patterns a custom day may name. */
    patterns: z.array(movementPattern),
    protocols: z.array(z.object({
        /** `D19`: every number below is a finding these records carry. */
        evidenceIds: z.array(z.string()),
        goal: z.string(),
        id: z.string(),
        isDefault: z.boolean(),
        /** The first week single-joint work appears (T19). */
        isolationFromWeek: z.int(),
        level: z.string(),
        nameKey: z.string(),
        repRange: z.object({ max: z.int(), min: z.int() }),
        restSeconds: z.object({ compound: z.int(), isolation: z.int() }),
        setsPerExercise: z.object({ from3: z.int(), weeks1to2: z.int() }),
        /** The protocol's own split — `by_days` derives it from the count. */
        split: z.string(),
        /** What its own split builds at 2–6 days, day by day. */
        weeks: z.array(agentProtocolWeek),
    })),
    /** Every split a request may name, the days it needs, and what it builds
     *  at each day count it fits. */
    splits: z.array(z.object({
        minDays: z.int(),
        split: planSplit,
        weeks: z.array(agentProtocolWeek),
    })),
    /** The weekly volume the generator aims each muscle at (`D212`). */
    weeklySetTargets: z.object({
        floor: z.number(),
        normal: z.object({ max: z.number(), min: z.number() }),
        priority: z.object({ max: z.number(), min: z.number() }),
    }),
});
/** One session of the draft: the address the handoff needs, and the engine's
 *  own label for it so the model can say which day it is offering. */
export const agentDraftSession = z.object({
    /** The patterns the engine built the day as (`D213`) — compare them, and
     *  the label, with what was agreed before describing the draft. */
    focus: z.array(movementPattern).optional(),
    /** The engine's label — `"Full body A"`, `"Upper"`. Not localized: it is the
     *  blueprint's own string, and the app renders its own (`D101`). */
    label: z.string(),
    /** What `openScreen`'s `sessionId` must be, verbatim. */
    sessionId: z.string(),
});
export const agentPlanDraftResult = z.discriminatedUnion("ok", [
    z.object({
        /** The plan this call wrote. What `openScreen` carries, and the ONLY thing
         *  that may — never the parameters that produced it (`T-145`, `D150`). */
        draftId: z.string(),
        /** The protocol's evidence record identifiers (`D19`, `D102`). Every
         *  number the model states about this plan cites one of these. */
        evidenceIds: z.array(z.string()),
        ok: z.literal(true),
        /** The catalogue id the engine applied — resolved from the request, the
         *  incumbent or the default, so it is not necessarily what was asked. */
        protocolId: z.string(),
        /** The i18n key for its displayed name (`D101`). The app resolves it; the
         *  model writes its own sentence in the conversation's language. */
        protocolNameKey: z.string(),
        /** What the engine could not honour (`D212`): i18n keys and params —
         *  a muscle under the floor, a pattern it could not fill. Say them. */
        notices: z.array(planFinding).optional(),
        /** How many training days the draft has. The number the model says out
         *  loud, and the length of `sessions`. */
        sessionCount: z.int(),
        sessions: z.array(agentDraftSession).max(MAX_DRAFT_SESSIONS),
        /** The split the engine BUILT (`D213`) — never merely what was asked. */
        split: planSplit.optional(),
    }),
    agentWriteRefusal,
]);
/**
 * `recordWorkoutSet` — one set on the execution log (`D69`).
 *
 * ── NO `sessionId`, AND NO `setNumber` ──────────────────────────────────────
 * The write surface names both; neither can be a parameter here, for two different
 * reasons.
 *
 * `setNumber` is not an input to the USE CASE either — the session assigns it,
 * one past its last visible set, because a client choosing its own would
 * collide with a set the trainer logged from the other phone thirty seconds
 * earlier.
 *
 * `sessionId` is the more important one. `requireSessionAccess` checks a
 * session against the ACTOR's reach and not against the conversation's subject
 * — correctly, since a session id says nothing about whose log it is — so a
 * trainer's conversation stamped for one student could reach another student's
 * open session by naming it. That is precisely the property `D72` exists to
 * hold, and the fix is not to add a check on top of an identifier the model
 * supplies: it is to not take one. The far side resolves the subject's own
 * open session and opens one if there is none.
 */
export const recordWorkoutSetRequest = z.strictObject({
    /** Metres covered — a carry, a sled (`D151`). */
    distanceMetres: z
        .number()
        .positive()
        .max(MAX_SET_DISTANCE_METRES)
        .nullable()
        .optional(),
    /** Seconds held — a plank, a hang (`D151`). */
    durationSeconds: z
        .int()
        .min(1)
        .max(MAX_SET_DURATION_SECONDS)
        .nullable()
        .optional(),
    /** A catalogue id, from {@link findExercisesRequest}. */
    exerciseId: z.string().min(1).max(64),
    /** Kilograms as the person counts them; nothing converts. `null` for a
     *  bodyweight movement, which is not the same fact as `0`. */
    load: z.number().min(0).max(MAX_LOAD).nullable().optional(),
    /** The day it was performed. Omitted means now. */
    performedAt: calendarDay.optional(),
    reps: z.int().min(1).max(MAX_REPS).nullable().optional(),
    /** Rate of perceived exertion, halves allowed. */
    rpe: z.number().min(0).max(10).nullable().optional(),
});
export const agentWorkoutSetWriteResult = z.discriminatedUnion("ok", [
    z.object({
        ok: z.literal(true),
        performedAt: z.string(),
        /** True when no session was open and this call opened one. The person's
         *  own screen will find it open; see the note on the far side. */
        sessionStarted: z.boolean(),
        /** Assigned by the session, never chosen. */
        setNumber: z.number(),
    }),
    agentWriteRefusal,
]);
/* ────────────────────────────────────────────────────────────────────────────
 * THE PROFILE, THE CONFIG AND THE EQUIPMENT (`D149`)
 *
 * Three more writes, and the line they stop at. `updateProfile`,
 * `setTrainingConfig` and `setEquipment` are what turns *"change my goal"*
 * from a conversation with no hands into a write the change feed can show.
 * Every rule above holds unchanged: no account field, strict object, `agent`
 * stamped by the far side, a refusal as a result.
 *
 * ── WHAT IS DELIBERATELY NOT HERE ───────────────────────────────────────────
 * No request over `athlete_injury` and none over `parq_response`. The
 * prompt's "Do not treat" section says pain and injury never reach the model,
 * and a medical questionnaire in a model turn is a different product. When
 * the person mentions either, the trainer names the body map or the
 * questionnaire in settings — and writes nothing.
 *
 * ── THE CHANGE FEED SHOWS THE LAST WRITE ONLY ───────────────────────────────
 * Profile, config and equipment are one row per person, replaced in place;
 * only the measurement series and the plan keep history. So the feed shows
 * the latest write's attribution and nothing before it, and each tool's
 * description says so, because a model that believes a rewrite is
 * recoverable will offer to undo what it cannot.
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * `updateProfile` — the fields named are written, the rest are left (`D149`).
 *
 * Every field is optional: *"I'm 1.80 now"* is one fact and must not require
 * the model to restate ten others it may have wrong. `null` clears, on the
 * `motivation` precedent (`D129`, `D148`). The two the FIRST write needs —
 * `dateOfBirth` and `sex` — are refused by the far side with a key the model
 * can relay when no profile exists yet.
 *
 * `dateOfBirth` is a calendar day and lands at UTC MIDNIGHT — the profile
 * route's rule, so the derived age cannot slip a day at a boundary. That is
 * NOT the measurement rule (midday): a birthday is a date, a weigh-in is an
 * event, and `agent-entrypoint.ts` converts each on its own line.
 *
 * The bounds are the settings screen's own (`profileInput`), so nothing the
 * agent writes is a value the screen could not have.
 */
export const updateProfileRequest = z.strictObject({
    dateOfBirth: calendarDay.optional(),
    heightCm: z.int().min(100).max(250).nullable().optional(),
    /** The person's own sentence — tone and horizon, never a prescription
     *  (`D19`). */
    motivation: lifestyleShape.motivation.optional(),
    occupation: lifestyleShape.occupation.optional(),
    previousModalities: lifestyleShape.previousModalities.optional(),
    proteinMeals: lifestyleShape.proteinMeals.optional(),
    proteinSupplement: lifestyleShape.proteinSupplement.optional(),
    sex: z.enum(ATHLETE_SEXES).optional(),
    sleepHoursWeekday: lifestyleShape.sleepHoursWeekday.optional(),
    sleepHoursWeekend: lifestyleShape.sleepHoursWeekend.optional(),
    sleepQuality: lifestyleShape.sleepQuality.optional(),
});
export const agentProfileWriteResult = z.discriminatedUnion("ok", [
    z.object({
        /** True when this call wrote the person's FIRST profile. */
        created: z.boolean(),
        /** The field names the call carried — what the model may say it
         *  changed, and nothing it did not. */
        fields: z.array(z.string()),
        ok: z.literal(true),
    }),
    agentWriteRefusal,
]);
/**
 * `setTrainingConfig` — the WHOLE config, as `PUT /me/training-config` takes
 * it (`D149`).
 *
 * THE SAME SCHEMA, imported and not restated: one row replaced whole, with
 * the four cross-field checks (`fixedWeekdays` against `daysPerWeek`, the
 * secondary goal against the primary) that a restatement would drift from.
 * It is the heaviest tool schema in the set because the experience tree is a
 * discriminated union — and that is the point: a flat shape would admit
 * `{ status: "never", stoppedAgo: "gt3y" }`, a row the engine would read and
 * silently believe. The model states every field or the call is refused —
 * there is no partial config, because the engine reads the row whole.
 */
export const setTrainingConfigRequest = trainingConfigInput;
export const agentConfigWriteResult = z.discriminatedUnion("ok", [
    z.object({
        /**
         * Every field the write CHANGED, old → new (`D211`) — what the answer
         * must report. On 2026-10-03 the trainer rewrote a person's setup in
         * silence to steer a draft; with the before beside the after, nothing it
         * changed can go unsaid. Absent from an older server.
         */
        changes: z
            .array(z.object({ field: z.string(), from: z.unknown(), to: z.unknown() }))
            .optional(),
        /** DERIVED by the server from the experience tree (`D103`) — the one
         *  fact the model could not have written and may now read back. */
        level: z.enum(TRAINING_LEVELS),
        ok: z.literal(true),
        updatedAt: z.string(),
    }),
    agentWriteRefusal,
]);
/**
 * `setEquipment` — the WHOLE list, as `PUT /me/equipment` takes it (`D149`).
 *
 * Anything absent is removed, which is what a checklist means
 * (`equipment.ts` says why a delta API is worse). Ids come from
 * {@link findEquipmentRequest}; an id the catalogue does not have is refused
 * on the far side with a key the model can relay, rather than as the foreign
 * key's constraint failure crossing the binding as a crash.
 *
 * `min(1)`, on `upsertPlanItems`'s argument: an empty list is every piece of
 * equipment removed in one call, and no request the model receives means it.
 * "Just body weight" is a one-element list.
 */
export const setEquipmentRequest = z.strictObject({
    available: z
        .array(z.int().positive())
        .min(1)
        .max(EQUIPMENT_ID_COUNT)
        // The PUT's own check, restated because the aggregate refuses a repeat
        // too (`equipment.id_repeated`) — refused here, the model is handed the
        // field and fixes the call; refused there, it is handed a key to relay
        // to a person who never typed the list.
        .refine((ids) => new Set(ids).size === ids.length, {
        error: "available must not repeat an equipment id",
    }),
});
export const agentEquipmentWriteResult = z.discriminatedUnion("ok", [
    z.object({
        /** How many pieces the person now has — the whole list's length. */
        availableCount: z.number(),
        ok: z.literal(true),
    }),
    agentWriteRefusal,
]);
/* ────────────────────────────────────────────────────────────────────────────
 * ONE ONBOARDING ANSWER, GIVEN IN CHAT (plan D31, repo D171)
 *
 * The stepper stays the onboarding path (`D126`). What this adds is the case
 * where the conversation touches ONE fact the intake is still missing and the
 * person answers it in words: the trainer writes that answer through the SAME
 * step → section mapping the stepper uses (`intakeWrites` in
 * `@repo/contract/training`), so the two writers cannot store different state
 * for the same answer.
 *
 * ── `value` IS THE INTAKE'S OWN SHAPE ───────────────────────────────────────
 * A list of strings, exactly as `IntakeAnswers` holds it: one entry for a
 * single choice, a date or a number, one per selection for the weekdays and
 * the equipment. The far side hands it to the mapping unchanged, so there is
 * no second encoding of an answer to drift from the first.
 *
 * ── THE SCREEN-ONLY STEPS ARE NOT IN THE ENUM ───────────────────────────────
 * `parq` and `current_pain` are health answers: the PAR-Q is a medical
 * questionnaire and the pain step leads to the body map. The prompt says they
 * are screen-only and this schema is what makes that true — a model that
 * reaches for one is refused by the parse, not by a sentence it may ignore.
 * ──────────────────────────────────────────────────────────────────────────── */
/** The two steps only their own screens collect. */
export const SCREEN_ONLY_ONBOARDING_STEPS = ["parq", "current_pain"];
/** Every other progress step: what `recordOnboardingAnswer` may write. */
export const ONBOARDING_ANSWER_STEPS = [
    "date_of_birth",
    "sex",
    "height",
    "weight",
    "goal",
    "secondary_goal",
    "days_per_week",
    "days_fixed",
    "fixed_weekdays",
    "session_minutes",
    "session_hard_limit",
    "equipment_place",
    "equipment",
    "experience",
];
/** A step's option values, from the intake's own table — never restated. */
function choiceValues(step) {
    const [first, ...rest] = ONBOARDING_CHOICE_VALUES[step] ?? [];
    if (first === undefined) {
        throw new Error(`the intake has no option values for step ${step}`);
    }
    return [first, ...rest];
}
/** One entry, from the step's options. */
function oneChoice(step) {
    return z.array(z.enum(choiceValues(step))).length(1);
}
/** A whole number or one decimal, written out — the intake's number answer. */
const DECIMAL = /^\d{1,3}(\.\d)?$/;
function numberWithin(min, max, whole) {
    return z
        .array(z
        .string()
        .regex(DECIMAL)
        .refine((raw) => {
        const value = Number(raw);
        return (value >= min &&
            value <= max &&
            (!whole || Number.isInteger(value)));
    }, { error: `must be between ${min} and ${max}` }))
        .length(1);
}
/** A `catalog_equipment` id written out, or one of the four home chips. */
const EQUIPMENT_ENTRY = new RegExp(`^([1-9]\\d*|${HOME_EQUIPMENT_OPTIONS.map((option) => option.value).join("|")})$`);
const answer = (step, value) => z.strictObject({ step: z.literal(step), value });
/**
 * `recordOnboardingAnswer` — one step's answer (§14 of the plan's contracts).
 *
 * A discriminated union on `step` so each step carries ITS vocabulary into the
 * tool's JSON Schema: the model is shown the goals when it answers `goal` and
 * a date when it answers `date_of_birth`, and a value from another step's list
 * is refused at the parse.
 */
export const recordOnboardingAnswerRequest = z.discriminatedUnion("step", [
    answer("date_of_birth", z.array(calendarDay).length(1)),
    answer("sex", oneChoice("sex")),
    // Whole centimetres, the number step's own bounds (`wire-contracts.md` §4).
    answer("height", numberWithin(100, 250, true)),
    // Kilograms, one decimal.
    answer("weight", numberWithin(30, 300, false)),
    answer("goal", oneChoice("goal")),
    answer("secondary_goal", oneChoice("secondary_goal")),
    answer("days_per_week", oneChoice("days_per_week")),
    answer("days_fixed", oneChoice("days_fixed")),
    answer("fixed_weekdays", z
        .array(z.enum(choiceValues("fixed_weekdays")))
        .min(1)
        .max(7)
        .refine((days) => new Set(days).size === days.length, {
        error: "fixed_weekdays must not repeat a day",
    })),
    answer("session_minutes", oneChoice("session_minutes")),
    answer("session_hard_limit", oneChoice("session_hard_limit")),
    answer("equipment_place", oneChoice("equipment_place")),
    answer("equipment", z
        .array(z.string().regex(EQUIPMENT_ENTRY))
        .min(1)
        .max(EQUIPMENT_ID_COUNT)
        .refine((entries) => new Set(entries).size === entries.length, {
        error: "equipment must not repeat an entry",
    })),
    answer("experience", oneChoice("experience")),
]);
export const agentOnboardingAnswerResult = z.discriminatedUnion("ok", [
    z.object({
        ok: z.literal(true),
        step: z.enum(ONBOARDING_ANSWER_STEPS),
        /**
         * The sections this answer wrote, in write order. EMPTY means the answer
         * was valid but does not decide a section on its own yet — a profile with
         * no date of birth stored, a fixed week with no days — so nothing was
         * written. The stepper is where the rest of that section is answered.
         */
        written: z.array(z.enum(INTAKE_WRITE_SECTIONS)),
    }),
    agentWriteRefusal,
]);
/* ────────────────────────────────────────────────────────────────────────────
 * THE CONVERSATION INDEX (`D78` · `D79`)
 *
 * ⚠️ THE TWO SCHEMAS BELOW ARE NOT TOOLS, AND MUST NEVER BECOME ONE.
 *
 * Every other request schema in this file becomes a tool's JSON Schema and is
 * the vocabulary a model may write in. These two are the RUNTIME's own calls:
 * `TrainerAgent` makes them from `onConnect` and from the message path, and the
 * model never sees them, never names them and cannot cause one with any text it
 * produces. Adding one of them to a `ToolSet` would hand the model a way to
 * register or touch a conversation of its own choosing.
 *
 * ── WHY THIS CROSSES THE BINDING AT ALL ────────────────────────────────────
 * A Durable Object is reached BY NAME and Cloudflare offers no way to list the
 * objects in a namespace, so a conversation that never wrote its row is
 * unreachable by deletion and by export, forever — there is no reconciliation
 * pass, at any price (`D78`). `apps/ai` has no D1 binding and must not grow
 * one, so the row is written from here, and a failure must fail the
 * conversation rather than be swallowed.
 *
 * ── AND WHAT THEY DO NOT CARRY ─────────────────────────────────────────────
 * No account field, for the reason at the top of this file — the actor is the
 * first parameter and the runtime injects it from the stamped scope. And **no
 * message content**, ever: the transcript stays in the object, because writes
 * are the expensive D1 metric and two copies of the same text would have to be
 * kept in agreement.
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * The conversation half of `agentInstanceName(accountId, conversationId)` — see
 * `../iam/ws-ticket.ts`, whose `CONVERSATION_ID_PATTERN` the edge has already
 * applied. Restated here rather than imported so this file keeps describing the
 * whole of what crosses the binding; the bound is the same 64 characters.
 */
const conversationIdField = z.string().min(1).max(64);
export const registerConversationRequest = z.strictObject({
    conversationId: conversationIdField,
    /**
     * PROVENANCE, never authority — the group the subject was reached through,
     * off the roster row `apps/api` itself produced (`ConversationScope`). The
     * far side verifies it against a managing membership anyway and refuses one
     * the actor does not hold, because it is a real foreign key and a log line
     * naming a group that had nothing to do with the conversation is worse than
     * no group at all.
     */
    groupId: z.string().min(1).max(64).nullable().optional(),
});
export const agentConversationResult = z.object({
    conversationId: z.string(),
    /** `D79`. The trainer's conversation about a former student: readable, and
     *  no new message. The caller gates its message path on this. */
    frozen: z.boolean(),
    /** `false` when the row was already there — a reconnect or a hibernation
     *  wake rather than a new conversation. */
    registered: z.boolean(),
    subjectAccountId: z.string().nullable(),
});
export const touchConversationRequest = z.strictObject({
    conversationId: conversationIdField,
});
export const notifyTrainerReplyRequest = z.strictObject({
    conversationId: conversationIdField,
});
export const agentTrainerReplyNotifyResult = z.object({
    /** Registrations the push went to. `0` is an account that never enabled
     *  push, and is not an error. */
    devices: z.number().int().nonnegative(),
});
export const agentConversationTouchResult = z.object({
    /** `true` when the conversation is frozen and NOTHING was written (`D79`). */
    frozen: z.boolean(),
});
//# sourceMappingURL=data-access.js.map